/**
 * ComponentLazyLoader - Lazy loads web components on-demand
 * Scans for unregistered custom elements and loads them based on priority and visibility
 */

class ComponentLazyLoader {
  static instance = null;
  static loadingStates = new Map(); // Track loading state of components
  static loadedComponents = new Set(); // Track successfully loaded components
  static failedComponents = new Set(); // Track failed component loads
  static observer = null; // Intersection Observer instance
  static mutationObserver = null; // Watch for new DOM elements

  constructor() {
    console.log("ComponentLazyLoader Constructor");

    if (ComponentLazyLoader.instance) {
      return ComponentLazyLoader.instance;
    }

    this.observedElements = new WeakSet(); // Track elements we're already observing
    this.elementsByType = new Map(); // Track elements by component type for cleanup
    this.loadingPromises = new Map(); // Track loading promises for parallel loading

    ComponentLazyLoader.instance = this;
    this.init();
  }

  init() {
    this.setupIntersectionObserver();
    this.setupMutationObserver();
    this.scanExistingElements();
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: "50px", // Start loading 50px before element is visible
      threshold: 0,
    };

    ComponentLazyLoader.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.loadComponentForElement(entry.target);
          ComponentLazyLoader.observer.unobserve(entry.target);
        }
      });
    }, options);
  }

  setupMutationObserver() {
    ComponentLazyLoader.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              this.scanElement(node);
              // Also scan child elements
              this.scanElementTree(node);
            }
          });
        }
      });
    });

    ComponentLazyLoader.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  scanExistingElements() {
    this.scanElementTree(document.body);
  }

  scanElementTree(root) {
    // Get all custom elements (elements with hyphens in tag name)
    const customElements = root.querySelectorAll("*");

    customElements.forEach((element) => {
      this.scanElement(element);
    });

    // Also check the root element itself
    this.scanElement(root);
  }

  scanElement(element) {
    if (!element || element.nodeType !== Node.ELEMENT_NODE) return;

    const tagName = element.tagName.toLowerCase();

    // Check if it's a custom element (contains hyphen) and not already registered
    if (tagName.includes("-") && !customElements.get(tagName)) {
      this.handleUnregisteredElement(element);
    }
  }

  handleUnregisteredElement(element) {
    const tagName = element.tagName.toLowerCase();

    console.log("[ComponentLazyLoader] handleUnregisteredElement", tagName);

    // Skip if we've already failed to load this component type
    if (ComponentLazyLoader.failedComponents.has(tagName)) {
      return;
    }

    // Skip if already being handled
    if (this.observedElements.has(element)) {
      return;
    }

    this.observedElements.add(element);

    // Track this element by type for cleanup
    if (!this.elementsByType.has(tagName)) {
      this.elementsByType.set(tagName, new Set());
    }
    this.elementsByType.get(tagName).add(element);

    // Add loading state
    element.classList.add("component-loading");
    element.setAttribute("data-component-state", "loading");

    // Check if it should be loaded immediately
    const shouldPrioritize =
      element.getAttribute("data-loading") === "prioritize";

    if (shouldPrioritize) {
      this.loadComponentForElement(element);
    } else {
      // Observe for intersection
      ComponentLazyLoader.observer.observe(element);
    }
  }

  async loadComponentForElement(element) {
    const tagName = element.tagName.toLowerCase();

    console.log("[ComponentLazyLoader] loadComponentForElement", tagName);

    // Skip if already loaded
    if (ComponentLazyLoader.loadedComponents.has(tagName)) {
      this.updateElementState(element, "loaded");
      return;
    }

    // If already loading, wait for existing promise
    if (this.loadingPromises.has(tagName)) {
      try {
        await this.loadingPromises.get(tagName);
        this.updateElementState(element, "loaded");
      } catch (error) {
        console.log('LoadingComponentError:', error);
        
        this.updateElementState(element, "error");
      }
      return;
    }

    // Start loading (parallel)
    ComponentLazyLoader.loadingStates.set(tagName, "loading");
    const loadingPromise = this.loadComponent(tagName);
    this.loadingPromises.set(tagName, loadingPromise);

    try {
      await loadingPromise;
      ComponentLazyLoader.loadedComponents.add(tagName);

      console.log(
        "[ComponentLazyLoader] loadComponentForElement",
        tagName,
        "loaded"
      );

      // Clean up observers for ALL elements of this type
      this.cleanupObserversForType(tagName);

      // Update all elements of this type
      this.updateAllElementsOfType(tagName, "loaded");
    } catch (error) {
      console.warn(`Failed to load component "${tagName}":`, error);
      ComponentLazyLoader.failedComponents.add(tagName);

      // Update all elements of this type
      this.updateAllElementsOfType(tagName, "error");
    } finally {
      ComponentLazyLoader.loadingStates.delete(tagName);
      this.loadingPromises.delete(tagName);
    }
  }

  cleanupObserversForType(tagName) {
    // Get all elements of this type that we're tracking
    const elementsOfType = this.elementsByType.get(tagName);

    if (elementsOfType) {
      elementsOfType.forEach((element) => {
        // Stop observing this element
        ComponentLazyLoader.observer.unobserve(element);
      });

      // Clear the tracking set
      elementsOfType.clear();
    }
  }

  async loadComponent(tagName) {
    console.log("[ComponentLazyLoader] loadComponent", tagName);

    // Convert tag name to import map key
    const importKey = `@controllers/${tagName}`;

    try {
      // Dynamic import using import maps
      const module = await import(importKey);

      // The component should auto-register itself via BaseController
      if (module.default && typeof module.default === "function") {
        // Component loaded successfully - BaseController will handle registration
        return module.default;
      } else {
        throw new Error(
          `Component "${tagName}" does not export a default class`
        );
      }
    } catch (error) {
      if (error.message.includes("Failed to resolve module specifier")) {
        throw new Error(`Import map entry "@controllers/${tagName}" not found`);
      }
      throw error;
    }
  }

  updateElementState(element, state) {
    element.classList.remove(
      "component-loading",
      "component-loaded",
      "component-error"
    );
    element.classList.add(`component-${state}`);
    element.setAttribute("data-component-state", state);
  }

  updateAllElementsOfType(tagName, state) {
    const elements = document.querySelectorAll(tagName);
    elements.forEach((element) => {
      this.updateElementState(element, state);
    });
  }

  async waitForLoadingSlot() {
    // Removed - we're using parallel loading now
    return Promise.resolve();
  }

  // Public API methods
  static init() {
    return new ComponentLazyLoader();
  }

  static scan(element = document.body) {
    if (ComponentLazyLoader.instance) {
      ComponentLazyLoader.instance.scanElementTree(element);
    }
  }

  static async loadComponent(tagName) {
    if (ComponentLazyLoader.instance) {
      const dummyElement = document.createElement(tagName);
      await ComponentLazyLoader.instance.loadComponentForElement(dummyElement);
    }
  }

  static isLoaded(tagName) {
    return ComponentLazyLoader.loadedComponents.has(tagName);
  }

  static isLoading(tagName) {
    return ComponentLazyLoader.instance?.loadingPromises.has(tagName) || false;
  }

  static hasFailed(tagName) {
    return ComponentLazyLoader.failedComponents.has(tagName);
  }

  // Cleanup method
  static destroy() {
    if (ComponentLazyLoader.observer) {
      ComponentLazyLoader.observer.disconnect();
      ComponentLazyLoader.observer = null;
    }

    if (ComponentLazyLoader.mutationObserver) {
      ComponentLazyLoader.mutationObserver.disconnect();
      ComponentLazyLoader.mutationObserver = null;
    }

    if (ComponentLazyLoader.instance) {
      ComponentLazyLoader.instance.elementsByType.clear();
      ComponentLazyLoader.instance.loadingPromises.clear();
    }

    ComponentLazyLoader.instance = null;
    ComponentLazyLoader.loadingStates.clear();
    ComponentLazyLoader.loadedComponents.clear();
    ComponentLazyLoader.failedComponents.clear();
  }

  // Manual trigger methods for advanced usage
  static prioritizeElement(element) {
    if (
      ComponentLazyLoader.instance &&
      !ComponentLazyLoader.instance.observedElements.has(element)
    ) {
      element.setAttribute("data-loading", "prioritize");
      ComponentLazyLoader.instance.handleUnregisteredElement(element);
    }
  }

  static observeElement(element) {
    if (
      ComponentLazyLoader.instance &&
      !ComponentLazyLoader.instance.observedElements.has(element)
    ) {
      ComponentLazyLoader.instance.handleUnregisteredElement(element);
    }
  }
}

// Auto-initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    ComponentLazyLoader.init();
  });
} else {
  ComponentLazyLoader.init();
}

export default ComponentLazyLoader;
